import * as Enumerable from "linq";
import { autoinject } from "aurelia-dependency-injection";
import { noView } from "aurelia-templating";
import { customAttribute } from "aurelia-templating";
import { bindable } from "aurelia-templating";
import { computedFrom } from "aurelia-binding";
import * as DOMPurify from "dompurify";
import { Reflection } from "../helpers/reflection";
import { LookupClientService } from "../services/lookups/lookup-client.service";
import { LookupValue, LookupValueRequest, LookupValueResponse } from "../models/lookup.models";

@customAttribute("lookup-property")
@noView
@autoinject
export class LookupPropertyCustomAttribute {

    value: string;

    @computedFrom(Reflection.getPropertyPath<LookupPropertyCustomAttribute>(z => z.value))
    get valueAsClass(): string {
        return this.value ? Reflection.kebabCase(this.value) : '';
    }

    @bindable
    public id: number;

    @bindable
    public query: string;

    @bindable
    public parameters: string;

    @bindable
    public format: string = "{name}";

    @bindable
    public fallback: string = null;

    constructor(private element: Element,
        private lookupsClientService: LookupClientService) {
    }

    public idChanged(): void {
        this.invalidate();
    }

    public formatChanged(): void {
        this.invalidate();
    }

    public queryChanged(): void {
        this.invalidate();
    }

    public parametersChanged(): void {
        this.invalidate();
    }

    private invalidate(): void {
        if (this.query && this.id) {
            const lookupValueRequest = this.createLookupValueRequest();
            this.lookupsClientService.getLookup(lookupValueRequest, "default")
                .then((response: LookupValueResponse) => {
                    if (response) {
                        const item = Enumerable.from(response.allValuesIncludingInactive).firstOrDefault(x => x.id == this.id);
                        let value = this.getString(item);
                        if (!value)
                            value = this.searchById(this.id, response.allValuesIncludingInactive, 0);
                        this.element.innerHTML = DOMPurify.sanitize(value);
                        this.value = value;
                        if (value) {
                            $(this.element).addClass(value.toLocaleLowerCase());
                        }
                    } else {
                        this.element.innerHTML = "";
                        this.value = '';
                    }
                }, () => {
                    this.element.innerHTML = "";
                    this.value = '';
                });
        } else {
            this.element.innerHTML = DOMPurify.sanitize(this.fallback);
            this.value = '';
        }
    }

    private searchById(id: number, lookupValues: LookupValue[], level: number): string {
        if (lookupValues) {
            for (const lookupValue of lookupValues) {
                const value: LookupValue = lookupValue.id == id ? lookupValue : null;
                if (value) {
                    return this.getString(value);
                }
                else if (lookupValue.children && lookupValue.children.length > 0) {
                    const childrenResult = this.searchById(id, lookupValue.children, level + 1);
                    if (childrenResult !== null) {
                        return childrenResult;
                    }
                }
            }
        }
        return null;
    }

    getString(item: LookupValue): string {
        if (!item) return "";

        let str = this.format;

        for (const x in item) {
            const pattern = new RegExp("{" + x + "}", "g");
            str = str.replace(pattern, item[x]);
        }

        if (item.additionalData)
            for (const x in item.additionalData) {
                const pattern = new RegExp("{" + x + "}", "g");
                str = str.replace(pattern, item.additionalData[x]);
            }

        return str;
    }

    protected createLookupValueRequest(): LookupValueRequest {
        const request = new LookupValueRequest(this.query, this.parameters);
        return request;
    }

    bind() {
        this.invalidate()
    }
}
